import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import type { Method } from 'axios';
import { message } from 'antd';
import apiClient from '../../../services/apiClient';
import { devApi } from '../../../services/devApi';
import type {
  ObserveWindow,
  ObserveAuditItem,
  ObserveReceipt,
  ObserveExpectedJson,
  ObserveHandoffCheck,
  ObserveHandoffResult,
} from '../../../services/devApi';

// ============================================================
// Constants & pure helpers
// ============================================================

export interface ChainOption {
  value: string;
  label: string;
}

/** v1 只開放 CHAIN-03，保留陣列結構方便擴充 */
export const CHAIN_OPTIONS: ChainOption[] = [
  { value: 'CHAIN-03', label: 'CHAIN-03 銷售出貨鏈' },
];

const POLL_INTERVAL_MS = 3000;
const AUDIT_PAGE_SIZE = 100;

/** 預設測試單號前綴：CH03L + MMDD（如 CH03L0611） */
export function genDocPrefix(): string {
  const now = new Date();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `CH03L${mm}${dd}`;
}

/** scenarioId：CHAIN03-LIVE-{YYYYMMDD}-{HHmm} */
export function genScenarioId(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  return `CHAIN03-LIVE-${yyyy}${mm}${dd}-${hh}${min}`;
}

/** ops 欄位防禦性格式化（後端理論上回 string[]，但容忍字串） */
export function fmtOps(ops: string[] | string | null | undefined): string {
  if (ops === null || ops === undefined) return '--';
  return Array.isArray(ops) ? ops.join(', ') : String(ops);
}

export function formatDateTime(val: string | null | undefined): string {
  if (!val) return '--';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return val;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${mm}-${dd} ${hh}:${min}:${ss}`;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

function buildSummaryMarkdown(
  receipt: ObserveReceipt,
  handoffResults: Record<string, ObserveHandoffResult>,
  docNo: string
): string {
  const w = receipt.window;
  const { matched, missing, unexpected } = receipt.comparison;
  const checks = receipt.expected?.handoffChecks ?? [];
  const lines: string[] = [];

  lines.push('# 資料流觀測驗證摘要');
  lines.push('');
  lines.push('## 觀察窗');
  lines.push('| 項目 | 值 |');
  lines.push('| --- | --- |');
  lines.push(`| Window ID | #${w.id} |`);
  lines.push(`| Chain | ${w.chainId} |`);
  lines.push(`| Scenario | ${w.scenarioId} |`);
  lines.push(`| 單號前綴 | ${w.docPrefix} |`);
  lines.push(`| 實際單號 | ${docNo || '--'} |`);
  lines.push(`| 觀察期間 | ${formatDateTime(w.startedAt)} ~ ${formatDateTime(w.stoppedAt)} |`);
  lines.push('');

  lines.push('## Expected vs Actual 比對');
  if (missing.length === 0 && unexpected.length === 0) {
    lines.push('結論：✅ Actual Write Set 與 Expected 完全吻合');
  } else {
    lines.push(`結論：❌ 缺寫 ${missing.length} 表、未知多寫 ${unexpected.length} 表`);
  }
  lines.push('');

  lines.push(`### 吻合 (${matched.length})`);
  lines.push('| 表 | Expected Ops | Actual Ops |');
  lines.push('| --- | --- | --- |');
  matched.forEach((m) => lines.push(`| ${m.table} | ${fmtOps(m.expectedOps)} | ${fmtOps(m.actualOps)} |`));
  lines.push('');

  lines.push(`### 缺寫 (${missing.length})`);
  lines.push('| 表 | Expected Ops | 說明 |');
  lines.push('| --- | --- | --- |');
  missing.forEach((m) => lines.push(`| ${m.table} | ${fmtOps(m.expectedOps)} | ${m.note || '--'} |`));
  lines.push('');

  lines.push(`### 未知多寫 (${unexpected.length})`);
  lines.push('| 表 | 來源 | 新增 | 更新 | 刪除 |');
  lines.push('| --- | --- | --- | --- | --- |');
  unexpected.forEach((u) =>
    lines.push(`| ${u.table} | ${u.source || '--'} | ${u.inserts} | ${u.updates} | ${u.deletes} |`)
  );
  lines.push('');

  const results = checks.map((c) => handoffResults[c.id]).filter(Boolean);
  const passCount = results.filter((r) => r.pass).length;
  lines.push(`## 交棒驗證 (${passCount}/${checks.length} PASS)`);
  lines.push('| 檢查 | URL | HTTP | 結果 | 詳情 |');
  lines.push('| --- | --- | --- | --- | --- |');
  checks.forEach((c) => {
    const r = handoffResults[c.id];
    if (r) {
      lines.push(`| ${c.name} | ${r.url} | ${r.httpStatus || '--'} | ${r.pass ? 'PASS' : 'FAIL'} | ${r.detail} |`);
    } else {
      lines.push(`| ${c.name} | ${c.urlTemplate} | -- | 未執行 | -- |`);
    }
  });
  lines.push('');

  return lines.join('\n');
}

// ============================================================
// Hook
// ============================================================

export interface UseFlowObserveResult {
  // control state
  chainId: string;
  setChainId: (v: string) => void;
  docPrefix: string;
  setDocPrefix: (v: string) => void;
  scenarioId: string;
  initializing: boolean;
  starting: boolean;
  stopping: boolean;
  // window / live state
  obsWindow: ObserveWindow | null;
  isActive: boolean;
  auditCount: number;
  audits: ObserveAuditItem[];
  expected: ObserveExpectedJson | null;
  // receipt state
  receipt: ObserveReceipt | null;
  receiptLoading: boolean;
  // handoff state
  docNo: string;
  setDocNo: (v: string) => void;
  handoffResults: Record<string, ObserveHandoffResult>;
  runningCheckId: string | null;
  runningAll: boolean;
  // actions
  start: () => Promise<void>;
  stop: () => Promise<void>;
  runOne: (check: ObserveHandoffCheck) => Promise<void>;
  runAll: () => Promise<void>;
  copySummary: () => Promise<void>;
}

export function useFlowObserve(): UseFlowObserveResult {
  const [chainId, setChainId] = useState<string>(CHAIN_OPTIONS[0].value);
  const [docPrefix, setDocPrefix] = useState<string>(genDocPrefix());
  const [scenarioId, setScenarioId] = useState<string>(genScenarioId());
  const [initializing, setInitializing] = useState(true);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);

  const [obsWindow, setObsWindow] = useState<ObserveWindow | null>(null);
  const [auditCount, setAuditCount] = useState(0);
  const [audits, setAudits] = useState<ObserveAuditItem[]>([]);
  const [expected, setExpected] = useState<ObserveExpectedJson | null>(null);

  const [receipt, setReceipt] = useState<ObserveReceipt | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const [docNo, setDocNo] = useState<string>(genDocPrefix());
  const [handoffResults, setHandoffResults] = useState<Record<string, ObserveHandoffResult>>({});
  const handoffResultsRef = useRef<Record<string, ObserveHandoffResult>>({});
  const [runningCheckId, setRunningCheckId] = useState<string | null>(null);
  const [runningAll, setRunningAll] = useState(false);

  const isActive = obsWindow?.status === 'active';

  const recordResult = useCallback((r: ObserveHandoffResult) => {
    handoffResultsRef.current = { ...handoffResultsRef.current, [r.checkId]: r };
    setHandoffResults(handoffResultsRef.current);
  }, []);

  const resetHandoff = useCallback(() => {
    handoffResultsRef.current = {};
    setHandoffResults({});
  }, []);

  const loadReceipt = useCallback(async (windowId: number) => {
    setReceiptLoading(true);
    try {
      const r = await devApi.observeReceipt(windowId);
      setReceipt(r);
      setExpected(r.expected);
    } catch (err) {
      console.error('Load observe receipt error:', err);
      message.error('載入回執報告失敗');
    } finally {
      setReceiptLoading(false);
    }
  }, []);

  // 頁面載入：恢復活動窗
  useEffect(() => {
    (async () => {
      try {
        const cur = await devApi.observeCurrent();
        if (cur.window) {
          setObsWindow(cur.window);
          setAuditCount(cur.auditCount);
          setChainId(cur.window.chainId);
          setDocPrefix(cur.window.docPrefix);
          setDocNo(cur.window.docPrefix);
          setScenarioId(cur.window.scenarioId);
        }
      } catch (err) {
        console.error('Fetch current observe window error:', err);
      } finally {
        setInitializing(false);
      }
    })();
  }, []);

  // 觀察中：載入 expected（白名單表用於即時回執標色）
  useEffect(() => {
    if (!isActive || !obsWindow) return;
    devApi
      .observeExpected(obsWindow.chainId)
      .then(setExpected)
      .catch((err) => console.error('Fetch observe expected error:', err));
  }, [isActive, obsWindow?.chainId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 觀察中：每 3 秒輪詢 current + audits
  useEffect(() => {
    if (!isActive || !obsWindow) return;
    const windowId = obsWindow.id;
    let cancelled = false;

    const tick = async () => {
      try {
        const [cur, aud] = await Promise.all([
          devApi.observeCurrent(),
          devApi.observeAudits(windowId, { size: AUDIT_PAGE_SIZE }),
        ]);
        if (cancelled) return;
        setAuditCount(cur.auditCount);
        setAudits(aud.items || []);
        if (!cur.window || cur.window.status !== 'active' || cur.window.id !== windowId) {
          // 觀察窗已在別處被結束
          setObsWindow((prev) => (prev ? { ...prev, status: 'stopped' } : prev));
          message.info('觀察窗已結束，載入回執報告');
          loadReceipt(windowId);
        }
      } catch (err) {
        console.error('Poll observe window error:', err);
      }
    };

    tick();
    const timer = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [isActive, obsWindow?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = useCallback(async () => {
    const sid = genScenarioId();
    setScenarioId(sid);
    setStarting(true);
    try {
      const res = await devApi.observeStart({ chainId, scenarioId: sid, docPrefix });
      setObsWindow(res.window);
      setReceipt(null);
      resetHandoff();
      setAudits([]);
      setAuditCount(0);
      setDocNo(docPrefix);
      message.success('觀察窗已開始，請開始操作 ERP 建立測試單據');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        const w = (err.response.data as { window?: ObserveWindow } | undefined)?.window;
        if (w) {
          setObsWindow(w);
          setChainId(w.chainId);
          setDocPrefix(w.docPrefix);
          setDocNo(w.docPrefix);
          setScenarioId(w.scenarioId);
        }
        message.warning('已有進行中的觀察窗，已自動載入');
      } else {
        console.error('Start observe window error:', err);
        message.error('開始觀察失敗');
      }
    } finally {
      setStarting(false);
    }
  }, [chainId, docPrefix, resetHandoff]);

  const stop = useCallback(async () => {
    if (!obsWindow) return;
    setStopping(true);
    try {
      const res = await devApi.observeStop(obsWindow.id);
      setObsWindow(res.window);
      message.success('觀察已結束，產生回執報告中…');
      await loadReceipt(res.window.id);
    } catch (err) {
      console.error('Stop observe window error:', err);
      message.error('結束觀察失敗');
    } finally {
      setStopping(false);
    }
  }, [obsWindow, loadReceipt]);

  // 執行單一交棒驗證（不丟例外，結果記錄到 handoffResults）
  const executeCheck = useCallback(
    async (check: ObserveHandoffCheck): Promise<ObserveHandoffResult> => {
      const target = docNo.trim();
      const url = check.urlTemplate.split('{docNo}').join(encodeURIComponent(target));
      let result: ObserveHandoffResult;
      try {
        const res = await apiClient.request({
          method: (check.method || 'GET').toLowerCase() as Method,
          url,
        });
        const body = JSON.stringify(res.data ?? '');
        const pass = res.status >= 200 && res.status < 300 && body.includes(target);
        result = {
          checkId: check.id,
          name: check.name,
          url,
          httpStatus: res.status,
          pass,
          detail: pass ? `回應包含單號 ${target}` : `HTTP ${res.status}，但回應未包含單號 ${target}`,
        };
      } catch (err) {
        const status = axios.isAxiosError(err) ? err.response?.status ?? 0 : 0;
        result = {
          checkId: check.id,
          name: check.name,
          url,
          httpStatus: status,
          pass: false,
          detail: status > 0 ? `HTTP ${status} 請求失敗` : '請求失敗（無回應）',
        };
      }
      recordResult(result);
      return result;
    },
    [docNo, recordResult]
  );

  const submitHandoff = useCallback(
    async (results: ObserveHandoffResult[]) => {
      const windowId = receipt?.window.id ?? obsWindow?.id;
      if (!windowId || results.length === 0) return;
      try {
        await devApi.observeHandoff(windowId, results);
        message.success('交棒驗證結果已存回後端');
      } catch (err) {
        console.error('Submit handoff results error:', err);
        message.error('交棒驗證結果儲存失敗');
      }
    },
    [receipt, obsWindow]
  );

  const runOne = useCallback(
    async (check: ObserveHandoffCheck) => {
      if (!docNo.trim()) {
        message.warning('請先輸入實際單號');
        return;
      }
      setRunningCheckId(check.id);
      try {
        await executeCheck(check);
        const checks = receipt?.expected?.handoffChecks ?? [];
        if (checks.length > 0 && checks.every((c) => handoffResultsRef.current[c.id])) {
          await submitHandoff(checks.map((c) => handoffResultsRef.current[c.id]));
        }
      } finally {
        setRunningCheckId(null);
      }
    },
    [docNo, executeCheck, receipt, submitHandoff]
  );

  const runAll = useCallback(async () => {
    if (!docNo.trim()) {
      message.warning('請先輸入實際單號');
      return;
    }
    const checks = receipt?.expected?.handoffChecks ?? [];
    if (checks.length === 0) return;
    setRunningAll(true);
    try {
      for (const c of checks) {
        setRunningCheckId(c.id);
        await executeCheck(c); // eslint-disable-line no-await-in-loop
      }
      await submitHandoff(checks.map((c) => handoffResultsRef.current[c.id]));
    } finally {
      setRunningAll(false);
      setRunningCheckId(null);
    }
  }, [docNo, receipt, executeCheck, submitHandoff]);

  const copySummary = useCallback(async () => {
    if (!receipt) return;
    const md = buildSummaryMarkdown(receipt, handoffResultsRef.current, docNo.trim());
    const ok = await copyText(md);
    if (ok) {
      message.success('驗證摘要 (Markdown) 已複製到剪貼簿');
    } else {
      message.error('複製到剪貼簿失敗');
    }
  }, [receipt, docNo]);

  return {
    chainId,
    setChainId,
    docPrefix,
    setDocPrefix,
    scenarioId,
    initializing,
    starting,
    stopping,
    obsWindow,
    isActive: !!isActive,
    auditCount,
    audits,
    expected,
    receipt,
    receiptLoading,
    docNo,
    setDocNo,
    handoffResults,
    runningCheckId,
    runningAll,
    start,
    stop,
    runOne,
    runAll,
    copySummary,
  };
}
